import React, { useState } from "react";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { BackButton, CreateButton } from "../../../../Components/Utils/Buttons";
import UseGetData from "../../../../Components/Hooks/useFetchData";
import DataTable from "../../../../Components/Others/DataTable";
import ViewMore from "./ViewMore";
import { MailPlus, Eye, X } from "lucide-react"; // Add Eye and X icons
import { HeadingWithIcon } from "../../../../Components/Utils/Settingheading";
import DOMPurify from "dompurify"; // Import DOMPurify for sanitizing HTML

const MainTemplate = () => {
  const [page, setPage] = useState("");
  const [pageCount, setPageCount] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const [selectedTemplate, setSelectedTemplate] = useState(null); // State for selected template
  const { data, totalCount, getData } = UseGetData(page, pageCount, "/email_templates/");

  let header = [
    {
      label: "ID",
      width: "w-[5%]",
      align: "text-center",
    },
    {
      label: "Subject",
      width: "w-[27%]",
      align: "text-left",
    },
    {
      label: "Message",
      width: "w-[35%]",
      align: "text-left",
    },
    // {
    //   label: "Thank Message",
    //   width: "w-[25%]",
    //   align: "text-left",
    // },
    {
      label: "Action",
      width: "w-[8%]",
      align: "text-center",
    },
  ];

  // Function to create an iframe srcdoc for rendering HTML content
  const createIframeContent = (htmlContent) => {
    if (!htmlContent) return "";
    // Sanitize the HTML content
    const sanitizedHtml = DOMPurify.sanitize(htmlContent, { ADD_TAGS: ["style"] });
    // Return the sanitized HTML as srcdoc for the iframe
    return sanitizedHtml;
  };

  // Function to format plain text message
  const formatPlainText = (message) => {
    if (!message) return "-";
    // Replace newlines with <br> for proper rendering
    return message.split("\n").map((line, idx) => (
      <span key={idx}>
        {line}
        <br />
      </span>
    ));
  };

  // Function to open the modal with the selected template
  const openModal = (item) => {
    setSelectedTemplate(item);
    setIsModalOpen(true);
  };

  // Function to close the modal
  const closeModal = () => {
    setSelectedTemplate(null);
    setIsModalOpen(false);
  };

  // Function to render the message content in the table
  const renderMessagePreview = (item) => {
    return (
      <button
        onClick={() => openModal(item)}
        className="flex items-center text-blue-500 hover:text-blue-700"
        title="Preview Email"
      >
        <Eye className="w-4 h-4 mr-1" />
        Preview
      </button>
    );
  };

  // Function to render the full template in the modal
  const renderFullTemplate = () => {
    if (!selectedTemplate) return null;

    if (selectedTemplate.html) {
      // For HTML content, use an iframe to render it safely with styles
      const iframeContent = createIframeContent(selectedTemplate.html);
      return (
        <iframe
          sandbox="allow-same-origin"
          srcDoc={iframeContent}
          className="w-full h-[80vh] border-none"
          title={`Email Template ${selectedTemplate.id}`}
        />
      );
    } else {
      // For plain text, render with basic formatting
      return (
        <div className="text-sm text-gray-700 p-4 bg-gray-50 rounded h-[80vh] overflow-y-auto">
          {formatPlainText(selectedTemplate.message)}
        </div>
      );
    }
  };

  return (
    <div className="w-full h-full px-4 py-2">
      <div className="w-full flex justify-between items-center">
        <Breadcrumb
          items={[
            { name: "Settings" },
            { name: "Template" },
          ]}
        />
        <div className="flex gap-3 items-center">
          {/* <CreateButton label="Template" /> */}
          <BackButton />
        </div>
      </div>
      <div className="w-full h-[calc(100vh-145px)] bg-white mt-3 mx-auto p-2 rounded-md overflow-y-hidden">
        <HeadingWithIcon icon={MailPlus} title="Email Templates" />

        <div className="w-full h-[calc(100vh-195px)]">
          <DataTable
            headers={header}
            total={totalCount}
            pageIndex={page}
            setPageIndex={setPage}
            dataCount={pageCount}
            setDataCount={setPageCount}
            height="h-[calc(100vh-210px)]"
            bodyHeight="max-h-[calc(100vh-280px)]"
          >
            {data &&
              data.map((item, index) => (
                <tr
                  className="odd:bg-white even:bg-slate-50 hover:text-baseColor-primary hover:shadow-md"
                  key={index}
                >
                  <td className="align-top py-3 px-1 text-left w-[5%]">
                    {item?.id || "#"}
                  </td>
                  <td className="align-top py-3 text-left w-[27%]">
                    <p className="line-clamp-2 overflow-hidden text-ellipsis">
                      {item?.subject || "-"}
                    </p>
                  </td>
                  <td className="align-top py-3 text-left w-[35%]">
                    {/* Render the preview button */}
                    {renderMessagePreview(item)}
                  </td>
                  {/* <td className="align-top py-3 px-1 text-left w-[25%]">
                    <p className="line-clamp-2 overflow-hidden text-ellipsis">
                      {item?.thanksMessage || "-"}
                    </p>
                  </td> */}
                  <td className="align-top py-3 text-left w-[8%] px-1">
                    <div className="mr-14 inline-block">
                      <ViewMore getData={getData} data={item} />
                    </div>
                  </td>
                </tr>
              ))}
          </DataTable>
        </div>
      </div>

      {/* Modal for displaying the full email template */}
      {isModalOpen && (
         <div className="fixed top-0 left-0 w-full h-full bg-gray-800 bg-opacity-50 z-[9999] flex justify-center items-center">
           <div className="animate-scale-in min-w-[70%] max-w-[80%] max-h-[80vh] bg-white py-4 px-6 rounded-md shadow-lg overflow-y-scroll">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                Email Template Preview - {selectedTemplate?.subject || "Untitled"}
              </h2>
              <button onClick={closeModal} className="text-red-500 hover:text-red-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-4">
              {renderFullTemplate()}
            </div>
            {/* Modal Footer */}
            {/* <div className="flex justify-end p-4 border-t">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default MainTemplate;